/**
 * Frontend mirror of the backend's synthetic `conversation/start` staged action
 * (http-gateway `lib/schemas/tools.ts`): an action whose work is a chat rather
 * than a write. Invoking it ("Let's start on this") opens a new chat in the
 * sidepanel with its composer holding the action, for the user to send, then
 * records on the action that it began. When the planner already ran the chat
 * in the background, invoking opens that chat instead.
 */
export const CONVERSATION_STAGED_TOOLSET = 'conversation'
export const CONVERSATION_STAGED_TOOLNAME = 'start'

export function isConversationStagedAction(action: { toolSet?: string; toolName?: string }): boolean {
  return action.toolSet === CONVERSATION_STAGED_TOOLSET && action.toolName === CONVERSATION_STAGED_TOOLNAME
}

/**
 * A conversation action the planner runs in its own chat, now or shortly: the
 * chat is the action's work, so the chat doesn't show the action's card.
 */
export function isActionRunInItsChat(action: { toolSet?: string; toolName?: string; inputData?: unknown }): boolean {
  if (!isConversationStagedAction(action)) return false
  const input = (action.inputData ?? {}) as { chatStarted?: unknown; draftsAhead?: unknown }
  return input.chatStarted === true || input.draftsAhead === true
}

/** A skill a conversation action's chat runs with, as its input names it. */
export interface ConversationSkill {
  id: string
  name: string
}

/** The skills a conversation action's chat runs with, read off its input. */
export function conversationActionSkills(action: { inputData?: unknown }): Array<ConversationSkill> {
  const skills = (action.inputData as { skills?: unknown } | undefined)?.skills
  if (!Array.isArray(skills)) return []
  return skills.filter(
    (skill): skill is ConversationSkill =>
      typeof skill?.id === 'string' && typeof skill?.name === 'string' && skill.name.trim() !== '',
  )
}

/** Asks the overlay sidepanel, from any window, to open a chat, and show it. */
export const START_CHAT_EVENT = 'sidepanel:startChat'

/** A chat the action already started: the sidepanel opens it as it stands. */
export interface StartedChatEventPayload {
  chatId: string
  /** The back-button label on the chat it opens. */
  sourceLabel: string
}

/** A new chat holding an unsent message. */
export interface NewChatEventPayload {
  /** The message the composer opens holding. */
  prompt: string
  /** Background attached alongside it, e.g. the transcript excerpt that raised the action. */
  context?: string
  /** What the context's chip reads. Absent, it reads as pasted text. */
  contextLabel?: string
  /** The back-button label on the chat it opens. */
  sourceLabel: string
  /** Skills the chat runs with. The prompt @-mentions each one. */
  skills?: Array<ConversationSkill>
}

export type StartChatEventPayload = StartedChatEventPayload | NewChatEventPayload

/** The chat a conversation action starts, read off its input. Undefined when the input has no prompt. */
export function conversationStartPayload(action: {
  inputData?: unknown
  title?: string
  conversationId?: string | null
}): StartChatEventPayload | undefined {
  const input = (action.inputData ?? {}) as { prompt?: unknown; context?: unknown; chatStarted?: unknown }
  if (input.chatStarted === true && action.conversationId) {
    return { chatId: action.conversationId, sourceLabel: 'Actions' }
  }
  const prompt = typeof input.prompt === 'string' && input.prompt.trim() ? input.prompt : action.title
  if (!prompt) return undefined
  const skills = conversationActionSkills(action)
  return {
    prompt: [...skills.map((skill) => `@${skill.name}`), prompt].join(' '),
    ...(typeof input.context === 'string' && input.context.trim() ? { context: input.context } : {}),
    sourceLabel: 'Actions',
    ...(skills.length > 0 ? { skills } : {}),
  }
}
